import { useMemo, useState } from 'react'
import { Bell, ChevronDown, ChevronUp } from 'lucide-react'
import { EmptyState } from '../../components/ui'
import type { NotificationItem } from './types'
import { groupConsecutiveNotifications } from './groupNotifications'
import { NotificationItemRow } from './NotificationItemRow'

interface NotificationListProps {
  items: NotificationItem[]
  compact?: boolean
  selectable?: boolean
  selectedIds?: Set<string>
  onToggleSelected?: (item: NotificationItem) => void
  onActivate: (item: NotificationItem) => void
  onArchive: (item: NotificationItem) => void
}
export function NotificationList({
  items,
  compact = false,
  selectable = false,
  selectedIds = new Set<string>(),
  onToggleSelected,
  onActivate,
  onArchive,
}: NotificationListProps) {
  const entries = useMemo(() => groupConsecutiveNotifications(items), [items])
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  if (items.length === 0) {
    return (
      <EmptyState
        icon={<Bell />}
        title="Пока ничего нового"
        className={compact ? 'py-10' : undefined}
      />
    )
  }

  return (
    <div>
      {entries.map((entry) => {
        if (entry.kind === 'single') {
          return (
            <NotificationItemRow
              key={String(entry.item.id)}
              item={entry.item}
              compact={compact}
              selectable={selectable}
              selected={selectedIds.has(String(entry.item.id))}
              onToggleSelected={onToggleSelected}
              onActivate={onActivate}
              onArchive={onArchive}
            />
          )
        }

        const isExpanded = expanded.has(entry.key)
        const first = entry.items[0]
        return (
          <div key={entry.key} className="border-b border-gray-100 last:border-b-0 dark:border-zinc-800">
            <button
              type="button"
              onClick={() => setExpanded((current) => {
                const next = new Set(current)
                if (next.has(entry.key)) next.delete(entry.key)
                else next.add(entry.key)
                return next
              })}
              className="flex w-full items-center gap-3 bg-white px-4 py-3 text-left hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:bg-zinc-900 dark:hover:bg-zinc-800"
              aria-expanded={isExpanded}
            >
              {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              <span className="min-w-0 flex-1 truncate text-sm font-semibold text-gray-900 dark:text-zinc-100">
                {first.title}
              </span>
              <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-semibold text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300">
                {entry.items.length}
              </span>
            </button>
            {isExpanded && entry.items.map((item) => (
              <NotificationItemRow
                key={String(item.id)}
                item={item}
                compact={compact}
                selectable={selectable}
                selected={selectedIds.has(String(item.id))}
                onToggleSelected={onToggleSelected}
                onActivate={onActivate}
                onArchive={onArchive}
              />
            ))}
          </div>
        )
      })}
    </div>
  )
}
